import { useMemo } from 'react';

import { useHistory } from 'react-router-dom';

import { MdRemoveRedEye } from 'react-icons/md';

import { Tooltip } from 'ui/components/tooltip';
import { TableBodyColumn } from 'ui/components/table';
import TableBodyRow from 'ui/components/table/TableBodyRow';

import ErrorIcon from 'assets/icons/list/ErrorIcon';
import PendingIcon from 'assets/icons/list/PendingIcon';
import SuccessIcon from 'assets/icons/list/SuccessIcon';
import UnavailableIcon from 'assets/icons/list/UnavailableIcon';

import { formatDateToLocaleDateString } from 'utils/form';

import { ModalProps } from 'consumerUnits/consumerUnitBillingCycles/pages/ListConsumerUnitBillingCycles';

import { ConsumerUnitBillingCycle } from 'consumerUnits/consumerUnitBillingCycles/models/consumerUnitBillingCycles';

import { ConsumerUnitBillingCaptureStatus } from 'consumerUnitBillingCapture/model/consumerUnitBillingCaptures';

type Props = {
  consumerUnitBillingCycles: ConsumerUnitBillingCycle;
  onClickOpenSideBarModal: (
    modal: ModalProps,
    consumerUnitBillingCycle: ConsumerUnitBillingCycle
  ) => void;
};

const DATE_CONFIG_OPTIONS = {
  options: { month: '2-digit', year: 'numeric' } as Intl.DateTimeFormatOptions,
};

const CREATE_ROUTE = '/dashboard/bill-data/consumer-units/create';

export default function ConsumerUnitBillingCyclesTableItem({
  onClickOpenSideBarModal,
  consumerUnitBillingCycles: {
    id,
    consumerUnit,
    consumerUnitBillingCapture,
    consumerUnitBillingCycleDate,
    powerDistributionUnitBillData,
  },
}: Props) {
  const { push } = useHistory();

  const {
    consumerUnitBillingCaptureStatus = ConsumerUnitBillingCaptureStatus.WAITING,
    consumerUnitBillingCaptureError,
  } = consumerUnitBillingCapture || {};

  const CONSUMER_UNIT_BILLING_CYCLE: ConsumerUnitBillingCycle = {
    id,
    consumerUnit,
    consumerUnitBillingCapture,
    consumerUnitBillingCycleDate,
    powerDistributionUnitBillData,
  };

  const onClickCreateButton = (
    pathname: string,
    consumerUnitBillingCycles: ConsumerUnitBillingCycle
  ) => {
    push({
      pathname,
      state: consumerUnitBillingCycles,
    });
  };

  const HAS_BILL_DATA_AND_BILLING_WAS_CAPTURED =
    !!powerDistributionUnitBillData &&
    consumerUnitBillingCaptureStatus === ConsumerUnitBillingCaptureStatus.SUCCESS;

  const consumerUnitBillingCaptureStatusErrorActived =
    consumerUnitBillingCaptureStatus === ConsumerUnitBillingCaptureStatus.ERROR;

  const consumerUnitBillingCaptureStatusWaitingActived =
    consumerUnitBillingCaptureStatus === ConsumerUnitBillingCaptureStatus.WAITING;

  const labelStatusIcon = useMemo(() => {
    const status = {
      [ConsumerUnitBillingCaptureStatus.ERROR]: `Erro: ${
        consumerUnitBillingCaptureError ||
        'Falha na captura da fatura, reporte ao time de Produto'
      }`,
      [ConsumerUnitBillingCaptureStatus.SUCCESS]:
        (HAS_BILL_DATA_AND_BILLING_WAS_CAPTURED && 'Detalhes da fatura') ||
        'Pendente de Informação',
      [ConsumerUnitBillingCaptureStatus.WAITING]: 'Fatura indisponível',
    };

    return status[consumerUnitBillingCaptureStatus];
  }, [
    HAS_BILL_DATA_AND_BILLING_WAS_CAPTURED,
    consumerUnitBillingCaptureError,
    consumerUnitBillingCaptureStatus,
  ]);

  return (
    <TableBodyRow dataTestId={`consumer-unit-billing-cycles-row-id-${id}`}>
      <TableBodyColumn>{consumerUnit.id}</TableBodyColumn>
      <TableBodyColumn>
        {formatDateToLocaleDateString(consumerUnitBillingCycleDate, {
          year: DATE_CONFIG_OPTIONS.options.year,
          month: DATE_CONFIG_OPTIONS.options.month,
        })}
      </TableBodyColumn>
      <TableBodyColumn>
        {consumerUnit.cooperativeMember.cooperativeMemberLegalName}
      </TableBodyColumn>
      <TableBodyColumn>
        {consumerUnit.consumerUnitPowerDistributionUnitIdentifier}
      </TableBodyColumn>
      <TableBodyColumn
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Tooltip
          placement="bottom"
          label={'Carteira de Creditação'}
          disabled={!HAS_BILL_DATA_AND_BILLING_WAS_CAPTURED}
        >
          <MdRemoveRedEye
            size={25}
            onClick={() =>
              HAS_BILL_DATA_AND_BILLING_WAS_CAPTURED &&
              onClickOpenSideBarModal('creditsWallet', {
                id,
                consumerUnit,
                consumerUnitBillingCapture,
                consumerUnitBillingCycleDate,
                powerDistributionUnitBillData,
              })
            }
          />
        </Tooltip>
      </TableBodyColumn>
      <TableBodyColumn
        style={{
          position: 'relative',
        }}
      >
        <Tooltip
          placement="bottom"
          label={'Carteira de Economia'}
          disabled={!HAS_BILL_DATA_AND_BILLING_WAS_CAPTURED}
        >
          <MdRemoveRedEye
            size={25}
            onClick={() =>
              HAS_BILL_DATA_AND_BILLING_WAS_CAPTURED &&
              onClickOpenSideBarModal('economyWallet', {
                id,
                consumerUnit,
                consumerUnitBillingCapture,
                consumerUnitBillingCycleDate,
                powerDistributionUnitBillData,
              })
            }
            style={{
              top: '50%',
              right: '50%',
              position: 'absolute',
              transform: 'translate(50%, -50%)',
            }}
          />
        </Tooltip>
      </TableBodyColumn>
      <TableBodyColumn className="flex items-center justify-center">
        <Tooltip label={labelStatusIcon} placement="bottom">
          {consumerUnitBillingCaptureStatusErrorActived && (
            <ErrorIcon
              OnClick={() =>
                onClickCreateButton(CREATE_ROUTE, CONSUMER_UNIT_BILLING_CYCLE)
              }
            />
          )}
          {consumerUnitBillingCaptureStatusWaitingActived && (
            <UnavailableIcon
              OnClick={() =>
                onClickCreateButton(CREATE_ROUTE, CONSUMER_UNIT_BILLING_CYCLE)
              }
            />
          )}
          {(HAS_BILL_DATA_AND_BILLING_WAS_CAPTURED && (
            <SuccessIcon
              OnClick={() =>
                onClickOpenSideBarModal('details', {
                  id,
                  consumerUnit,
                  consumerUnitBillingCapture,
                  consumerUnitBillingCycleDate,
                  powerDistributionUnitBillData,
                })
              }
            />
          )) ||
            (!consumerUnitBillingCaptureStatusErrorActived &&
              !consumerUnitBillingCaptureStatusWaitingActived && (
                <PendingIcon
                  OnClick={() =>
                    onClickCreateButton(CREATE_ROUTE, CONSUMER_UNIT_BILLING_CYCLE)
                  }
                />
              ))}
        </Tooltip>
      </TableBodyColumn>
    </TableBodyRow>
  );
}
